// Rewrite Exercises Data - Systematic mock data for sentence pattern rewriting practice
// This data structure is designed for easy migration to Supabase backend

export const rewriteExercisesData = {
  // Sample sentences with their DeepSeek API analysis results
  sampleSentences: [
    {
      id: 'sentence_001',
      original: "Your designs are world-famous and have inspired many designers",
      analysis: {
        mainPattern: "S+V+C and S+V+O",
        basicStructure: "Subject + Verb + Complement and Subject + Verb + Object",
        legendStructure: "Your designs(S) + are(V) + world-famous(C) and have inspired(V) + many designers(O)",
        detailedAnalysis: {
          subject: "Your designs",
          verb: "are, have inspired",
          directObject: "many designers",
          indirectObject: null,
          complement: "world-famous",
          adverbial: null
        },
        subordinateClauses: [],
        verbTense: "present, present perfect",
        specialFeatures: ["compound sentence"],
        rewriteOptions: {
          "S+V": "Your designs exist.",
          "S+V+O": "Your designs inspire many designers.",
          "S+V+C": "Your designs are world-famous.",
          "S+V+IO+DO": "Your designs give someone something.",
          "S+V+O+C": "Your designs make something good."
        }
      }
    },
    {
      id: 'sentence_002',
      original: "When our display books are in poor condition, we of course would try to offer a new copy of these books to our customers",
      analysis: {
        mainPattern: "S+V+O+IO",
        basicStructure: "Subject + Verb + Object + Indirect Object",
        legendStructure: "We(S) + would try to offer(V) + a new copy(O) + to our customers(IO)",
        detailedAnalysis: {
          subject: "we",
          verb: "would try to offer",
          directObject: "a new copy of these books",
          indirectObject: "to our customers",
          complement: null,
          adverbial: "of course"
        },
        subordinateClauses: ["When our display books are in poor condition"],
        verbTense: "conditional",
        specialFeatures: ["modal verb", "infinitive", "prepositional phrase"],
        rewriteOptions: {
          "S+V": "We try.",
          "S+V+O": "We offer books.",
          "S+V+C": "We are helpful.",
          "S+V+IO+DO": "We give customers books.",
          "S+V+O+C": "We make books available."
        }
      }
    },
    {
      id: 'sentence_003',
      original: "However, if a new copy is unavailable, we would offer a display copy at a discounted price",
      analysis: {
        mainPattern: "S+V+O+Adv",
        basicStructure: "Subject + Verb + Object + Adverbial",
        legendStructure: "We(S) + would offer(V) + a display copy(O) + at a discounted price(Adv)",
        detailedAnalysis: {
          subject: "we",
          verb: "would offer",
          directObject: "a display copy",
          indirectObject: null,
          complement: null,
          adverbial: "at a discounted price"
        },
        subordinateClauses: ["if a new copy is unavailable"],
        verbTense: "conditional",
        specialFeatures: ["modal verb", "conditional clause"],
        rewriteOptions: {
          "S+V": "We offer.",
          "S+V+O": "We offer copies.",
          "S+V+C": "We are generous.",
          "S+V+IO+DO": "We give customers copies.",
          "S+V+O+C": "We make copies affordable."
        }
      }
    },
    {
      id: 'sentence_004',
      original: "I believe the member of staff who served you is new, and was at that time unaware of these policies",
      analysis: {
        mainPattern: "S+V+O",
        basicStructure: "Subject + Verb + Object",
        legendStructure: "I(S) + believe(V) + the member of staff who served you is new(O)",
        detailedAnalysis: {
          subject: "I",
          verb: "believe",
          directObject: "the member of staff who served you is new",
          indirectObject: null,
          complement: null,
          adverbial: null
        },
        subordinateClauses: ["who served you", "and was at that time unaware of these policies"],
        verbTense: "present, past",
        specialFeatures: ["relative clause", "compound sentence"],
        rewriteOptions: {
          "S+V": "I believe.",
          "S+V+O": "I believe the staff member.",
          "S+V+C": "I am confident.",
          "S+V+IO+DO": "I tell you the truth.",
          "S+V+O+C": "I find the situation concerning."
        }
      }
    },
    {
      id: 'sentence_005',
      original: "We try and ensure that our service is always the best we can offer",
      analysis: {
        mainPattern: "S+V+O",
        basicStructure: "Subject + Verb + Object",
        legendStructure: "We(S) + try and ensure(V) + that our service is always the best we can offer(O)",
        detailedAnalysis: {
          subject: "We",
          verb: "try and ensure",
          directObject: "that our service is always the best we can offer",
          indirectObject: null,
          complement: null,
          adverbial: null
        },
        subordinateClauses: ["that our service is always the best we can offer"],
        verbTense: "present",
        specialFeatures: ["compound verb", "that-clause"],
        rewriteOptions: {
          "S+V": "We try.",
          "S+V+O": "We ensure quality.",
          "S+V+C": "We are committed.",
          "S+V+IO+DO": "We give customers service.",
          "S+V+O+C": "We make service excellent."
        }
      }
    }
  ],

  // Pattern instructions for each sentence structure type
  patternInstructions: {
    "S+V": "請將句子改寫為最簡單的主詞+動詞結構，描述基本動作或狀態。",
    "S+V+O": "請將句子改寫為主詞+動詞+受詞的結構，突出動作的對象。",
    "S+V+C": "請將句子改寫為主詞+動詞+補語的結構，描述狀態或屬性。",
    "S+V+IO+DO": "請將句子改寫為雙受詞結構，明確指出受益者和動作對象。",
    "S+V+O+C": "請將句子改寫為受詞補語結構，描述受詞的狀態或特徵。"
  },

  // Validation rules for each pattern
  validationRules: {
    "S+V": {
      minWords: 2,
      requiredElements: ["subject", "verb"],
      forbiddenElements: ["object", "complement"],
      feedback: {
        correct: "很好！您的句子符合 S + V 結構，包含了主詞和動詞。",
        incorrect: "建議檢查：S + V 結構應該包含主詞和動詞，避免受詞或補語。"
      }
    },
    "S+V+O": {
      minWords: 3,
      requiredElements: ["subject", "verb", "object"],
      forbiddenElements: ["complement"],
      feedback: {
        correct: "很好！您的句子符合 S + V + O 結構，包含了主詞、動詞和受詞。",
        incorrect: "建議檢查：S + V + O 結構應該包含主詞、動詞和受詞。"
      }
    },
    "S+V+C": {
      minWords: 3,
      requiredElements: ["subject", "linking_verb", "complement"],
      forbiddenElements: ["object"],
      feedback: {
        correct: "很好！您的句子符合 S + V + C 結構，使用了連綴動詞和補語。",
        incorrect: "建議檢查：S + V + C 結構需要連綴動詞（如 is, are, become）和補語。"
      }
    },
    "S+V+IO+DO": {
      minWords: 4,
      requiredElements: ["subject", "verb", "indirect_object", "direct_object"],
      forbiddenElements: ["complement"],
      feedback: {
        correct: "很好！您的句子符合雙受詞結構，包含了間接受詞和直接受詞。",
        incorrect: "建議檢查：雙受詞結構需要如 give, tell, show 等動詞，後接間接受詞和直接受詞。"
      }
    },
    "S+V+O+C": {
      minWords: 4,
      requiredElements: ["subject", "verb", "object", "complement"],
      forbiddenElements: [],
      feedback: {
        correct: "很好！您的句子符合受詞補語結構，描述了受詞的狀態。",
        incorrect: "建議檢查：受詞補語結構需要如 make, find, consider 等動詞，後接受詞和補語。"
      }
    }
  },

  // Exercise categories for organization
  categories: {
    "basic_patterns": {
      name: "基本句型",
      description: "包含 S+V, S+V+O, S+V+C 等基本句型練習",
      patterns: ["S+V", "S+V+O", "S+V+C"]
    },
    "advanced_patterns": {
      name: "進階句型",
      description: "包含雙受詞、受詞補語等進階句型練習",
      patterns: ["S+V+IO+DO", "S+V+O+C"]
    },
    "compound_sentences": {
      name: "複合句",
      description: "包含並列句、從句等複合句練習",
      patterns: ["S+V", "S+V+O", "S+V+C", "S+V+IO+DO", "S+V+O+C"]
    }
  },

  // Difficulty levels
  difficultyLevels: {
    "beginner": {
      name: "初級",
      description: "適合初學者，句子結構簡單",
      patterns: ["S+V", "S+V+O"]
    },
    "intermediate": {
      name: "中級",
      description: "適合有一定基礎的學習者",
      patterns: ["S+V", "S+V+O", "S+V+C"]
    },
    "advanced": {
      name: "高級",
      description: "適合進階學習者，包含複雜句型",
      patterns: ["S+V", "S+V+O", "S+V+C", "S+V+IO+DO", "S+V+O+C"]
    }
  }
};

// Helper function to generate exercises from sentences
export const generateExercisesFromSentences = (sentences, maxExercises = 5) => {
  return sentences.slice(0, maxExercises).map((sentence, index) => ({
    id: index + 1,
    sentence: sentence.original,
    originalAnalysis: sentence.analysis,
    examples: sentence.analysis.rewriteOptions,
    instructions: rewriteExercisesData.patternInstructions,
    category: sentence.analysis.specialFeatures?.includes('compound sentence') ? 'compound_sentences' : 'basic_patterns',
    difficulty: sentence.analysis.mainPattern.includes('and') ? 'advanced' : 'intermediate'
  }));
};

// Helper function to validate user answers
export const validateRewriteAnswer = (userAnswer, selectedPattern, validationRules) => {
  const answer = userAnswer.trim();
  const words = answer.split(/\s+/);
  const rule = validationRules[selectedPattern];
  
  if (!rule) {
    return {
      isCorrect: false,
      feedback: "未知的句型類型。"
    };
  }

  // Basic word count check
  if (words.length < rule.minWords) {
    return {
      isCorrect: false,
      feedback: rule.feedback.incorrect
    };
  }

  // Pattern-specific validation
  let isCorrect = true;
  
  switch (selectedPattern) {
    case 'S+V':
      isCorrect = !answer.includes(' to ') && !answer.includes(' is ') && !answer.includes(' are ');
      break;
    case 'S+V+O':
      isCorrect = !answer.includes(' is ') && !answer.includes(' are ');
      break;
    case 'S+V+C':
      isCorrect = (answer.includes(' is ') || answer.includes(' are ') || answer.includes(' become '));
      break;
    case 'S+V+IO+DO':
      isCorrect = (answer.includes(' give ') || answer.includes(' tell ') || answer.includes(' show '));
      break;
    case 'S+V+O+C':
      isCorrect = (answer.includes(' make ') || answer.includes(' find ') || answer.includes(' consider '));
      break;
    default:
      isCorrect = true;
  }

  return {
    isCorrect,
    feedback: isCorrect ? rule.feedback.correct : rule.feedback.incorrect
  };
}; 